/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
import type { FilterableIndicator } from "components/screener/v2/ScreenerFilterableIndicators";
import type { FilterCondition } from "components/screener/v2/filters/types";
import type { IRunScreenerInputQuery } from "components/screener/v2/query/__generated__/RunScreenerInputQuery.generated";
import uniqBy from "lodash/uniqBy";
import { useCallback, useReducer } from "react";
import type { IRunScreenerInput } from "types/graphqlTypes";

type FiltersReducerState = {
  binaryOperator: "AND" | "OR";
  activeFilters: FilterableIndicator[];
  filterConditions: FilterCondition[];
};
type FiltersReducerAction =
  | {
      type: "SET_ACTIVE_FILTERS";
      activeFilters: FilterableIndicator[];
    }
  | {
      type: "TOGGLE_ACTIVE_FILTER";
      indicator: FilterableIndicator;
      isSelected?: boolean;
    }
  | {
      type: "SET_FILTER_CONDITIONS";
      filterConditions: FilterCondition[];
    }
  | {
      type: "SET_BINARY_OPERATOR";
      binaryOperator: "AND" | "OR";
    }
  | {
      type: "REPLACE_STATE";
      state: FiltersReducerState;
    };
type RSI = NonNullable<IRunScreenerInputQuery["runScreenerInput"]>;
export function useFilters(runScreenerInput?: RSI | null, selectedIndicators: FilterableIndicator[] = []) {
  const [state, runAction] = useReducer((state: FiltersReducerState, action: FiltersReducerAction) => {
    if (action.type === "TOGGLE_ACTIVE_FILTER") {
      if (action.isSelected !== undefined && action.isSelected) {
        return {
          ...state,
          activeFilters: state.activeFilters.filter(({ objectId }) => objectId !== action.indicator.objectId),
          filterConditions: state.filterConditions.filter(({ identifier }) => identifier !== action.indicator.objectId),
        };
      }
      if (action.isSelected !== undefined && !action.isSelected) {
        return {
          ...state,
          activeFilters: [...state.activeFilters, action.indicator],
        };
      }
      return {
        ...state,
        activeFilters: uniqBy([...state.activeFilters, action.indicator], "objectId"),
      };
    }
    if (action.type === "SET_ACTIVE_FILTERS") {
      return {
        ...state,
        activeFilters: action.activeFilters,
        filterConditions: state.filterConditions.filter(({ identifier }) => {
          return action.activeFilters.some(({ objectId }) => objectId === identifier);
        }),
      };
    }
    if (action.type === "SET_FILTER_CONDITIONS") {
      return { ...state, filterConditions: action.filterConditions };
    }
    if (action.type === "SET_BINARY_OPERATOR") {
      return { ...state, binaryOperator: action.binaryOperator };
    }
    if (action.type === "REPLACE_STATE") {
      return action.state;
    }
    return state;
  }, deriveStateFromInput(runScreenerInput));
  const setActiveFilters = useCallback((activeFilters: FilterableIndicator[]) => {
    runAction({ type: "SET_ACTIVE_FILTERS", activeFilters });
  }, []);
  const setFilterConditions = useCallback((filterConditions: FilterCondition[]) => {
    runAction({ type: "SET_FILTER_CONDITIONS", filterConditions });
  }, []);
  const toggleActiveFilter = useCallback((indicator: FilterableIndicator, isSelected?: boolean) => {
    runAction({ type: "TOGGLE_ACTIVE_FILTER", indicator, isSelected });
  }, []);
  const setFiltersPipelineBinaryOperator = useCallback((binaryOperator: "AND" | "OR") => {
    runAction({ type: "SET_BINARY_OPERATOR", binaryOperator });
  }, []);
  const loadFiltersFromInput = useCallback((input: RSI) => {
    runAction({ type: "REPLACE_STATE", state: deriveStateFromInput(input) });
  }, []);
  const resetAllFilters = useCallback(() => {
    runAction({ type: "REPLACE_STATE", state: deriveStateFromInput(null) });
  }, []);
  return {
    activeFilters: state.activeFilters.filter((candidate) => {
      return selectedIndicators.some(({ objectId }) => candidate.objectId === objectId);
    }),
    filterConditions: state.filterConditions,
    filterConditionsBinaryOperator: state.binaryOperator,
    setActiveFilters,
    toggleActiveFilter,
    setFilterConditions,
    setFiltersPipelineBinaryOperator,
    loadFiltersFromInput,
    resetAllFilters,
  };
}

function deriveStateFromInput(runScreenerInput?: RSI | null) {
  const input = (runScreenerInput?.input || null) as IRunScreenerInput | null;
  if (!input) {
    return {
      binaryOperator: "AND",
      activeFilters: [],
      filterConditions: [],
    } satisfies FiltersReducerState;
  }
  return {
    binaryOperator: input.filterConditions?.operator || "AND",
    activeFilters: (runScreenerInput?.selectedIndicators || []).filter((candidate) => {
      return ((input.filterConditions?.conditions || []) as FilterCondition[]).some(({ identifier }) => {
        return identifier === candidate.objectId;
      });
    }),
    filterConditions: input.filterConditions?.conditions || [],
  } satisfies FiltersReducerState;
}
